/**
 * Which co-branding capabilities each plan unlocks.
 *
 * Free accounts get a real, usable co-brand (name, contact, one logo) so the
 * feature is never a dead end; the richer marketing surface — headshot,
 * links, accent color, office block, multiple saved profiles — is what a
 * membership buys. Gating is enforced twice: hidden/locked in the UI, and
 * stripped again server-side in `saveReportBranding`.
 */

import type { PlanTier } from "./billing-plans";
import {
  DEFAULT_PARTNER_ACCENT,
  DEFAULT_REPORT_BRANDING,
  type ReportBranding,
} from "./report-branding";

export type CoBrandFeature =
  | "logo"
  | "contact"
  | "footerText"
  | "headshot"
  | "links"
  | "accentColor"
  | "officeAddress"
  | "licenseLine"
  | "savedProfiles";

export interface CoBrandCapabilities {
  features: Set<CoBrandFeature>;
  /** How many reusable brand profiles the plan can store. */
  savedProfiles: number;
  label: string;
}

const FREE: CoBrandFeature[] = ["logo", "contact", "footerText"];
const PREMIUM: CoBrandFeature[] = [
  ...FREE,
  "headshot",
  "links",
  "accentColor",
  "officeAddress",
  "licenseLine",
  "savedProfiles",
];

export function coBrandCapabilities(tier: PlanTier | undefined): CoBrandCapabilities {
  switch (tier) {
    case "starter":
      return { features: new Set(PREMIUM), savedProfiles: 3, label: "Starter" };
    case "pro":
      return { features: new Set(PREMIUM), savedProfiles: 10, label: "Pro" };
    case "team":
      return { features: new Set(PREMIUM), savedProfiles: 50, label: "Team" };
    default:
      return { features: new Set(FREE), savedProfiles: 1, label: "Free" };
  }
}

export function canUse(caps: CoBrandCapabilities, feature: CoBrandFeature): boolean {
  return caps.features.has(feature);
}

/** Human copy for the pricing page and the locked-field tooltips. */
export const COBRAND_TIER_COPY: Record<PlanTier, string> = {
  free: "Co-branding: your name, contact line and one logo",
  starter: "Full co-branding: logo, headshot, links, accent color, 3 saved profiles",
  pro: "Full co-branding + 10 saved brand profiles for your whole team",
  team: "Full co-branding + 50 saved brand profiles and shared partner marks",
};

/**
 * Removes premium fields the caller's plan does not include. Used by the
 * server before persisting, so a hand-crafted request can't unlock anything.
 */
export function applyCoBrandCapabilities(
  branding: ReportBranding,
  caps: CoBrandCapabilities,
): ReportBranding {
  const out: ReportBranding = { ...branding };
  if (!canUse(caps, "headshot")) delete out.headshotDataUrl;
  if (!canUse(caps, "links")) out.links = [];
  if (!canUse(caps, "accentColor")) out.accentColor = DEFAULT_PARTNER_ACCENT;
  if (!canUse(caps, "officeAddress")) out.officeAddress = "";
  if (!canUse(caps, "licenseLine")) out.licenseLine = "";
  return out;
}

/** The demo partner used by the co-branding sample page. */
export const SAMPLE_COBRANDING: ReportBranding = {
  ...DEFAULT_REPORT_BRANDING,
  enabled: true,
  partnerName: "Riverbend Realty Group",
  partnerContact: "Dana Whitfield · Managing Broker",
  partnerPhone: "(812) 555-0134",
  partnerEmail: "dana@riverbendrealty.example",
  licenseLine: "Managing Broker, TN #RB-314882",
  officeAddress: "218 W Main St, Suite 3, Franklin, TN 37064",
  websiteUrl: "https://riverbendrealty.example",
  links: [
    { label: "Book a walkthrough", url: "https://riverbendrealty.example/schedule" },
    { label: "Current listings", url: "https://riverbendrealty.example/listings" },
  ],
  accentColor: "#1f5f5b",
  footerText: "Prepared for you by Riverbend Realty Group",
  extraNote:
    "Questions about an exception on this report? Call the office and we'll walk the parcel with you before your inspection period closes.",
};
